import React, {FunctionComponent, useState} from 'react';
import {User} from '/src/domain/shared/User';
import {Track} from '/src/domain/challenges/ChallengeAuditData';
import AuditButton from '/src/ui/challengeProtocol/audit/AuditButton';
import ViewTrackButton from '/src/ui/challengeProtocol/record/ViewTrackButton';
import AppealButton from './AppealButton';
import {Grid, Collapse} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {theme} from "/src/assets/theme";
import cs from 'classnames';
import {sec2hms} from '/src/utils/time-utils'
import {useTranslation} from 'react-i18next';

interface AuditTrackProps {
    track: Track;
    currentUser: User;
    challengeKey: string;
}


const AuditTrack: FunctionComponent<AuditTrackProps> = ({
    track,
    currentUser,
    challengeKey,
}) => {
    const {t} = useTranslation()
    const classes = useStyles()
    const [isExpanded, setExpanded] = useState(false)


    return (
        <>
            <div onClick={() => setExpanded(!isExpanded)}>
                <AuditButton
                    startTime={track.start_date_abs}
                    auditResult={track.auditResult}
                    isExpanded={isExpanded}
                />
            </div>
            <Collapse in={isExpanded} className={classes.collapse}>
                <div className={cs(classes.parametersBlock, 'parametersBlockChild')}>
                    <Grid container
                          className={classes.parameters}
                          spacing={1}
                    >
                        {track.stravats && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('loaded')}
                            </Grid>
                            <Grid item xs={7}
                                  className={cs(classes.parameterValue, {[classes.loadedNotRunWindow]: track.auditResult.audit === 'statusNotRunWindow'})}>
                                {track.stravats}
                            </Grid>
                        </Grid>}
                        {track.type && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('activityType')}
                            </Grid>
                            <Grid item xs={7} className={classes.parameterValue}>
                                {track.type}
                            </Grid>
                        </Grid>}
                        {track.moving_time && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('movingTime')}
                            </Grid>
                            <Grid item xs={7} className={classes.parameterValue}>
                                {sec2hms(track.moving_time)}
                            </Grid>
                        </Grid>}
                        {track.elapsed_time && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('elapsedTime')}
                            </Grid>
                            <Grid item xs={7} className={classes.parameterValue}>
                                {sec2hms(track.elapsed_time)}
                            </Grid>
                        </Grid>}
                        {track.pace && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('pace')}
                            </Grid>
                            <Grid item xs={7} className={classes.parameterValue}>
                                {sec2hms(Number(track.pace) * 60)}
                            </Grid>
                        </Grid>}
                        {
                            track.mtage &&
                            <Grid item container xs={12}>
                                <Grid item xs={5}>
                                    {t('time')}
                                </Grid>
                                <Grid item xs={7} className={classes.parameterValue}>
                                    {sec2hms(track.mtage)}
                                </Grid>
                            </Grid>
                        }
                        {
                            track.mt &&
                            <Grid item container xs={12}>
                                <Grid item xs={5}>
                                    {t('absolute')}
                                </Grid>
                                <Grid item xs={7} className={classes.parameterValue}>
                                    {sec2hms(track.mt)}
                                </Grid>
                            </Grid>
                        }
                        {
                            track.sko &&
                            <Grid item container xs={12}>
                                <Grid item xs={5}>
                                    {t('standardDeviation')}
                                </Grid>
                                <Grid item xs={7} className={classes.parameterValue}>
                                    {track.sko}
                                </Grid>
                            </Grid>
                        }
                        {
                            track.progress &&
                            <Grid item container xs={12}>
                                <Grid item xs={5}>
                                    {t('progress')}
                                </Grid>
                                <Grid item xs={7} className={classes.parameterValue}>
                                    {track.progress}
                                </Grid>
                            </Grid>
                        }
                        {track.distance && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('distance')}
                            </Grid>
                            <Grid item xs={7} className={classes.parameterValue}>
                                {track.distance}
                            </Grid>
                        </Grid>}
                        {track.start_date_abs && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('startMsk')}
                            </Grid>
                            <Grid item xs={7} className={classes.parameterValue}>
                                {track.start_date_abs}
                            </Grid>
                        </Grid>}
                        {track.stop_date_abs && <Grid item container xs={12}>
                            <Grid item xs={5}>
                                {t('finishMsk')}
                            </Grid>
                            <Grid item xs={7} className={classes.parameterValue}>
                                {track.stop_date_abs}
                            </Grid>
                        </Grid>}
                    </Grid>
                    <ViewTrackButton trackId={track.id}/>
                    <AppealButton 
                        track={track} 
                        currentUser={currentUser}
                        challengeKey={challengeKey}
                    />
                </div>
            </Collapse>
        </>

    )
}

const useStyles = makeStyles((muiTheme) => ({
    collapse: {
        '&:last-child': {
            '& .parametersBlockChild': {
                marginBottom: '-1.5rem'
            }
        }
    },

    parametersBlock: {
        padding: '1rem 1.5rem 1.5rem',
    },
    parameters: {
        paddingBottom: '1rem',
        fontWeight: 500,
        fontSize: '0.9rem'
    },
    parameterValue: {
        fontSize: '0.8rem'
    },
    loadedNotRunWindow: {
        color: theme.secondaryColor
    }
}))

export default AuditTrack